import { ReportDefinition, ReportColumn } from '../models/report-definition';
import { ReportRowVm } from '../models/report-row-vm';
import {
   asNumberOrNull,
   asString,
   formatCurrency
} from '../utils/report-formatting.utils';
import { getReportArray } from '../utils/report-json.utils';
import { getRealTyallsRowId } from '../utils/report-row-id.utils';

const LAYOUT_SUMMARY = 'summary';
const LAYOUT_DETAIL = 'detail';

const GUEST_CREDIT_LIMIT_COLUMNS: ReportColumn[] = [
   { key: 'room', header: 'ROOM', width: '76px', sort: { type: 'string' } },
   {
      key: 'guestName',
      header: 'GUEST NAME',
      width: '210px',
      maxChars: 70,
      sort: { type: 'string' }
   },
   { key: 'noPost', header: 'POST', width: '70px', sort: { type: 'string' } },
   {
      key: 'paymentWindows',
      header: 'WIN: PAY MTH [BAL]',
      width: '170px',
      sort: { type: 'string' }
   },
   {
      key: 'payee',
      header: 'PAYEE',
      width: '260px',
      maxChars: 130,
      sort: { type: 'string' }
   },
   {
      key: 'limitAndAuth',
      header: 'LIMIT + AUTH',
      width: '130px',
      sort: {
         type: 'number',
         getValue: (row) => row.cells['limitAndAuthRaw'],
         defaultDirection: 'desc'
      }
   },
   {
      key: 'balance',
      header: 'BALANCE',
      width: '90px',
      sort: {
         type: 'number',
         getValue: (row) => row.cells['balanceRaw'],
         defaultDirection: 'desc'
      }
   },
   {
      key: 'variance',
      header: 'VARIANCE',
      width: '90px',
      sort: {
         type: 'number',
         getValue: (row) => row.cells['varianceRaw']
      }
   }
];

const DETAIL_COLUMNS: ReportColumn[] = [
   ...GUEST_CREDIT_LIMIT_COLUMNS,
   { key: 'reviewStatus', header: 'PAYEE REVIEW', width: '190px' }
];

function formatAmount(value: unknown): string {
   const amount = asNumberOrNull(value);

   return amount === null
      ? ''
      : Math.abs(amount).toLocaleString('en-AU', {
           minimumFractionDigits: 2,
           maximumFractionDigits: 2
        });
}

function getRowId(row: any): string {
   return (
      getRealTyallsRowId(row) ||
      `gcl:${asString(row?.ReservationId)}:${asString(
         row?.ConfirmationNumber
      )}:${asString(row?.FolioWindowNo)}`
   );
}

function getPaymentMethod(row: any): string {
   return asString(row?.PaymentMethod).trim().toUpperCase();
}

function getReservationGroupKey(row: any): string {
   return (
      asString(row?.ReservationId) ||
      asString(row?.ConfirmationNumber) ||
      `${asString(row?.RoomNumber)}:${asString(row?.Name)}`
   );
}

function getFolioWindowNo(row: any): string {
   return asString(row?.FolioWindowNo);
}

function getFolioWindowSortValue(row: any): number {
   const windowNo = Number(row?.FolioWindowNo);

   return Number.isFinite(windowNo) ? windowNo : 9999;
}

function getPayee(row: any): string {
   return asString(row?.Payee) || asString(row?.RoutingInstructions) || '-';
}

function getBalance(row: any): number | null {
   return asNumberOrNull(row?.Balance);
}

function getCreditLimit(row: any): number | null {
   return asNumberOrNull(row?.CreditLimit);
}

function getTotalPositiveBalance(rows: any[]): number {
   return rows.reduce((total, row) => {
      const balance = getBalance(row);

      return balance !== null && balance > 0 ? total + balance : total;
   }, 0);
}

function getSummaryLimit(rows: any[]): number | null {
   const limits = rows
      .map((row) => getCreditLimit(row))
      .filter((limit): limit is number => limit !== null);

   return limits.length ? Math.max(...limits) : null;
}

function getSummaryVariance(rows: any[]): number | null {
   const summaryLimit = getSummaryLimit(rows);

   return summaryLimit === null ? null : summaryLimit - getTotalPositiveBalance(rows);
}

function joinUnique(values: string[], separator = '\n'): string {
   return Array.from(
      new Set(values.map((value) => value.trim()).filter(Boolean))
   ).join(separator);
}

function formatWindowPaymentWithBalance(rows: any[]): string {
   return joinUnique(
      rows.map((row) => {
         const windowNo = getFolioWindowNo(row);
         const paymentMethod = getPaymentMethod(row) || '-';
         const balance = formatAmount(getBalance(row));

         return windowNo
            ? `${windowNo}: ${paymentMethod} [${balance}]`
            : `${paymentMethod} [${balance}]`;
      })
   );
}

function formatWindowPayees(rows: any[]): string {
   return joinUnique(
      rows.map((row) => {
         const windowNo = getFolioWindowNo(row);
         const payee = getPayee(row);

         return windowNo ? `${windowNo}: ${payee}` : payee;
      })
   );
}

function groupRowsByReservation(rows: any[]): any[][] {
   const groups = new Map<string, any[]>();

   for (const row of rows) {
      const key = getReservationGroupKey(row);

      if (!key) {
         continue;
      }

      const existingRows = groups.get(key) ?? [];

      existingRows.push(row);
      groups.set(key, existingRows);
   }

   return Array.from(groups.values())
      .map((groupRows) =>
         [...groupRows].sort(
            (a, b) => getFolioWindowSortValue(a) - getFolioWindowSortValue(b)
         )
      )
      .sort((a, b) => {
         const firstA = a[0];
         const firstB = b[0];

         return (
            asString(firstA?.RoomNumber).localeCompare(
               asString(firstB?.RoomNumber)
            ) || asString(firstA?.Name).localeCompare(asString(firstB?.Name))
         );
      });
}

function getGroupRowId(rows: any[]): string {
   const first = rows[0];

   return (
      asString(first?.ReservationId) ||
      asString(first?.ConfirmationNumber) ||
      getRowId(first)
   );
}

function getSourceRowIds(rows: any[]): string[] {
   return Array.from(
      new Set(rows.map((row) => getRealTyallsRowId(row)).filter(Boolean))
   );
}

function isPayeeGuestMatch(row: any): boolean {
   if (typeof row?.PayeeMatchesGuest === 'boolean') {
      return row.PayeeMatchesGuest;
   }

   // Compatibility for historical generated reports without classification flags.
   const normalize = (name: string) =>
      name
         .trim()
         .replace(/\s*,\s*/g, ',')
         .replace(/\s+/g, ' ')
         .toUpperCase();
   const guestName = asString(row?.Name);
   const payeeName = asString(row?.Payee);

   return !!guestName && !!payeeName && normalize(guestName) === normalize(payeeName);
}

function isSummaryEligible(row: any): boolean {
   if (typeof row?.SummaryRiskEligible === 'boolean') {
      return row.SummaryRiskEligible;
   }

   // Earlier reports do not include the new flag. The cash rule uses the
   // known OPERA cash code; new reports use the property's PMS mapping.
   return isPayeeGuestMatch(row) || getPaymentMethod(row) === 'CA';
}

function hasGeneratedRisk(row: any): boolean {
   if (row?.HasRisk !== true && (!Array.isArray(row?.Risks) || !row.Risks.length)) {
      return false;
   }

   // Legacy grouped total-balance risks can have been attributed to a guest
   // window using a different payee's balance. Those totals are not reliable
   // under the new window-level Summary rule until regenerated by the worker.
   if (typeof row?.SummaryRiskEligible !== 'boolean') {
      const codes = Array.isArray(row?.RiskCodes) ? row.RiskCodes : [];

      if (codes.includes('GUEST_CREDIT_LIMIT_TOTAL_BALANCE_GT_THRESHOLD')) {
         return false;
      }
   }

   return true;
}

function getReviewStatus(row: any): string {
   const balance = asNumberOrNull(row?.Balance);

   if (isPayeeGuestMatch(row)) {
      return 'Guest/payee match';
   }

   if (getPaymentMethod(row) === 'CA' && balance !== null && balance > 0) {
      return 'Cash balance — review';
   }

   return 'Verify payee and guarantee';
}

function getReportRows(reportJson: unknown): any[] {
   return getReportArray(reportJson, 'GuestCreditLimit');
}

function flattenGroupedRows(
   reportJson: unknown,
   summaryOnly: boolean
): ReportRowVm[] {
   const groups = groupRowsByReservation(getReportRows(reportJson));

   return groups
      .filter(
         (groupRows) =>
            !summaryOnly ||
            groupRows.some((row) => hasGeneratedRisk(row) && isSummaryEligible(row))
      )
      .map((groupRows): ReportRowVm => {
         const first = groupRows[0];
         // Show every window together, as before. For Summary only, the
         // balance/variance reflect the windows responsible for Summary risks.
         const balanceRows = summaryOnly
            ? groupRows.filter(
                 (row) => hasGeneratedRisk(row) && isSummaryEligible(row)
              )
            : groupRows;
         const balance = getTotalPositiveBalance(balanceRows);
         const limit = getSummaryLimit(balanceRows);
         const variance = getSummaryVariance(balanceRows);

         return {
            // Preserve the existing reservation-level row and its source IDs.
            // The viewer intentionally disables per-window row actions here.
            rowId: `guest-credit-limit:${getGroupRowId(groupRows)}`,
            sourceRowIds: getSourceRowIds(groupRows),
            cells: {
               rowType: 'guestCreditLimit',
               room: asString(first?.RoomNumber),
               guestName: asString(first?.Name),
               noPost: asString(first?.NoPost),
               paymentWindows: formatWindowPaymentWithBalance(groupRows),
               payee: formatWindowPayees(groupRows),
               limitAndAuth: formatCurrency(limit),
               limitAndAuthRaw: limit,
               balance: formatCurrency(balance),
               balanceRaw: balance,
               variance: formatCurrency(variance),
               varianceRaw: variance,
               reviewStatus: joinUnique(groupRows.map((row) => getReviewStatus(row))),
               reservationId: asString(first?.ReservationId),
               confirmationNumber: asString(first?.ConfirmationNumber)
            }
         };
      });
}

function flattenSummaryRows(reportJson: unknown): ReportRowVm[] {
   return flattenGroupedRows(reportJson, true);
}

function flattenDetailRows(reportJson: unknown): ReportRowVm[] {
   return flattenGroupedRows(reportJson, false);
}

const FILTER = {
   fields: [
      { key: 'room', label: 'Room', type: 'string' as const },
      { key: 'guestName', label: 'Guest Name', type: 'string' as const },
      { key: 'noPost', label: 'No Post', type: 'string' as const },
      { key: 'paymentWindows', label: 'Payment Method', type: 'string' as const },
      { key: 'payee', label: 'Payee', type: 'string' as const },
      {
         key: 'balance',
         label: 'Balance',
         type: 'number' as const,
         getValue: (row: Pick<ReportRowVm, 'cells'>) => row.cells['balanceRaw']
      },
      {
         key: 'variance',
         label: 'Variance',
         type: 'number' as const,
         getValue: (row: Pick<ReportRowVm, 'cells'>) => row.cells['varianceRaw']
      }
   ]
};

export const GUEST_CREDIT_LIMIT_DEFINITION: ReportDefinition = {
   reportType: 'GuestCreditLimit',
   version: 8,
   title: 'Guest Credit Limit',
   sorting: { enabled: true, persist: true },
   header: { title: 'Guest Credit Limit' },
   rowChecks: {
      enabled: false,
      includeNestedRows: false,
      selectableRowTypes: ['guestCreditLimit']
   },
   annotations: {
      enabled: true,
      selectableRowTypes: ['guestCreditLimit']
   },
   columns: GUEST_CREDIT_LIMIT_COLUMNS,
   flattenRows: flattenSummaryRows,
   filter: FILTER,
   layouts: [
      {
         id: LAYOUT_SUMMARY,
         label: 'Summary',
         columns: GUEST_CREDIT_LIMIT_COLUMNS,
         flattenRows: flattenSummaryRows,
         notice:
            'Summary shows only qualifying guest/payee-matched and positive cash-balance risks. All windows for each room are shown together; Summary balances reflect only the risky windows. Review the Detail tab for other balances and payment guarantees.'
      },
      {
         id: LAYOUT_DETAIL,
         label: 'Detail',
         columns: DETAIL_COLUMNS,
         flattenRows: flattenDetailRows,
         notice:
            'All report folio windows are shown here. Unmatched payees are not automatically Companies or TravelAgents; verify their balances and guaranteed payment methods.'
      }
   ]
};
